// src/components/AssetIndexDataGrid.js

import * as React from 'react';
import PropTypes from 'prop-types';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import {
  Box,
  Button,
  Stack,
  createTheme,
  ThemeProvider,
  CssBaseline,
  TextField,
  MenuItem,
} from '@mui/material';
import { assetColumns } from './AssetIndexDataGridColumns';
import './AssetIndex.css';
import { getAllAssetEntries } from '../../../asset/AssetsRegistry';

const darkGridTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#000', // grid container background
      paper: '#1e1e1e', // inner surfaces
    },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          backgroundColor: '#121212',
        },
        columnHeaders: {
          backgroundColor: '#1e1e1e',
        },
      },
    },
  },
});

export default function AssetIndexDataGrid({ onClose }) {
  // Read from AssetRegistry's in-memory cache
  const assets = React.useMemo(() => {
    const entries = getAllAssetEntries();
    if (!Array.isArray(entries)) return [];
    return entries;
  }, []);

  console.log('AssetIndexDataGrid: assets from AssetRegistry:', assets);

  const [selectionModel, setSelectionModel] = React.useState([]);
  const [filteredAssets, setFilteredAssets] = React.useState(assets);

  // When the source assets change (on mount), reset the filtered list
  React.useEffect(() => {
    setFilteredAssets(assets);
  }, [assets]);

  const [filterState, setFilterState] = React.useState({
    type: '',
    fileType: '',
    language: '',
    projectToken: '',
  });

  const handleRowSelection = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleFilterChange = (fieldName, value) => {
    const newFilterState = {
      ...filterState,
      [fieldName]: value,
    };

    setFilterState(newFilterState);

    const updatedAssets = assets.filter((asset) => {
      return (
        (!newFilterState.type || asset.type === newFilterState.type) &&
        (!newFilterState.fileType || asset.fileType === newFilterState.fileType) &&
        (!newFilterState.language || asset.language === newFilterState.language) &&
        (!newFilterState.projectToken ||
          asset.projectToken === newFilterState.projectToken)
      );
    });

    console.log('Updated filtered assets:', updatedAssets);
    setFilteredAssets(updatedAssets);
  };

  // Build distinct value lists for simple dropdown filters
  const distinctValues = React.useMemo(() => {
    const byField = (field) => {
      const set = new Set();
      assets.forEach((row) => {
        if (row && row[field]) {
          set.add(row[field]);
        }
      });
      return Array.from(set);
    };

    return {
      type: byField('type'),
      fileType: byField('fileType'),
      language: byField('language'),
      projectToken: byField('projectToken'),
    };
  }, [assets]);

  return (
    <ThemeProvider theme={darkGridTheme}>
      <CssBaseline />
      <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
        {/* Filters Section and Action Button on the Same Line */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 1, alignItems: 'center' }}
          className="filter-container2"
        >
          {/* Action Button */}
          <Button
            variant="contained"
            onClick={() => {}}
            disabled={!selectionModel.length}
          >
            DELETE
          </Button>

          {/* Inline Filters Section (no external component) */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              label="Type"
              value={filterState.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              {distinctValues.type.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="File Type"
              value={filterState.fileType}
              onChange={(e) => handleFilterChange('fileType', e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              {distinctValues.fileType.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Language"
              value={filterState.language}
              onChange={(e) => handleFilterChange('language', e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              {distinctValues.language.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Project"
              value={filterState.projectToken}
              onChange={(e) =>
                handleFilterChange('projectToken', e.target.value)
              }
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              {distinctValues.projectToken.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </Stack>

        {/* DataGrid Section */}
        <div className="dataGridContainer">
          <DataGridPremium
            rows={filteredAssets}
            columns={assetColumns}
            getRowId={(row) => row.id}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
            checkboxSelection
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleRowSelection}
            initialState={{
              sorting: {
                sortModel: [{ field: 'createdAt', sort: 'desc' }],
              },
            }}
          />
        </div>
      </Box>
    </ThemeProvider>
  );
}

AssetIndexDataGrid.propTypes = {
  onClose: PropTypes.func.isRequired,
};
